import React, { useState } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';

// Identify the emotion the sender most likely felt behind each message.
// Science: reading emotional tone accurately is the core of empathy and
// is the strongest predictor of social coherence (Lopes et al., 2005).

const SCENARIOS_EN = [
  { msg: '"Thanks for helping me with that. I really needed it."',          answer: 'Grateful',   options: ['Grateful','Annoyed','Nervous','Proud'] },
  { msg: '"Oh, so you forgot again. That\'s fine. Really."',               answer: 'Hurt',        options: ['Excited','Hurt','Bored','Confused'] },
  { msg: '"I got the promotion! I still can\'t believe it."',              answer: 'Proud',       options: ['Anxious','Sad','Proud','Relieved'] },
  { msg: '"I don\'t know if this is the right decision. What do you think?"', answer: 'Uncertain', options: ['Uncertain','Angry','Happy','Jealous'] },
  { msg: '"Everyone keeps asking me the same thing. I\'m fine."',          answer: 'Overwhelmed', options: ['Amused','Overwhelmed','Grateful','Curious'] },
  { msg: '"You didn\'t have to do that for me. Seriously."',               answer: 'Touched',     options: ['Touched','Bored','Anxious','Disappointed'] },
  { msg: '"I worked on this for three weeks and nobody noticed."',         answer: 'Disappointed', options: ['Disappointed','Excited','Relieved','Calm'] },
  { msg: '"I keep thinking about what might go wrong tomorrow."',          answer: 'Anxious',     options: ['Anxious','Proud','Grateful','Amused'] },
  { msg: '"Why does this always happen to me?"',                           answer: 'Frustrated',  options: ['Frustrated','Excited','Content','Confused'] },
  { msg: '"We haven\'t spoken in a while. I miss our chats."',            answer: 'Lonely',      options: ['Angry','Lonely','Relieved','Bored'] },
];

const SCENARIOS_NL = [
  { msg: '"Dank je wel dat je me hebt geholpen. Ik had het echt nodig."',  answer: 'Dankbaar',      options: ['Dankbaar','Geïrriteerd','Nerveus','Trots'] },
  { msg: '"Oh, dus je bent het weer vergeten. Prima. Echt."',              answer: 'Gekwetst',      options: ['Opgewonden','Gekwetst','Verveeld','Verward'] },
  { msg: '"Ik heb de promotie! Ik kan het nog steeds niet geloven."',     answer: 'Trots',         options: ['Angstig','Verdrietig','Trots','Opgelucht'] },
  { msg: '"Ik weet niet of dit de juiste beslissing is. Wat denk jij?"',  answer: 'Onzeker',       options: ['Onzeker','Boos','Blij','Jaloers'] },
  { msg: '"Iedereen stelt me steeds dezelfde vraag. Het gaat goed."',     answer: 'Overweldigd',   options: ['Geamuseerd','Overweldigd','Dankbaar','Nieuwsgierig'] },
  { msg: '"Je had dat echt niet voor me hoeven doen. Serieus."',          answer: 'Ontroerd',      options: ['Ontroerd','Verveeld','Angstig','Teleurgesteld'] },
  { msg: '"Ik heb hier drie weken aan gewerkt en niemand merkte het."',   answer: 'Teleurgesteld', options: ['Teleurgesteld','Opgewonden','Opgelucht','Rustig'] },
  { msg: '"Ik blijf maar denken aan wat er morgen mis kan gaan."',        answer: 'Angstig',       options: ['Angstig','Trots','Dankbaar','Geamuseerd'] },
  { msg: '"Waarom overkomt mij dit altijd?"',                              answer: 'Gefrustreerd',  options: ['Gefrustreerd','Opgewonden','Tevreden','Verward'] },
  { msg: '"We hebben al een tijdje niet gesproken. Ik mis onze gesprekken."', answer: 'Eenzaam', options: ['Boos','Eenzaam','Opgelucht','Verveeld'] },
];

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function ToneDecoderGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const SCENARIOS = nl ? SCENARIOS_NL : SCENARIOS_EN;
  const ROUNDS = SCENARIOS.length;

  const [phase, setPhase]   = useState('idle'); // idle | playing | done
  const [round, setRound]   = useState(0);
  const [score, setScore]   = useState(0);
  const [picked, setPicked] = useState(null);
  const [shuffled, setShuffled] = useState([]);
  const [results, setResults]   = useState([]); // {correct: bool} per round
  const [best, setBest] = useStorage('gm_tonedecoder_best', 0);

  const current = SCENARIOS[round];

  const startGame = () => {
    setPhase('playing'); setRound(0); setScore(0);
    setPicked(null); setResults([]);
    setShuffled(shuffle(SCENARIOS[0].options));
  };

  const pick = (option) => {
    if (picked !== null) return;
    setPicked(option);
    const correct = option === current.answer;
    const newScore = score + (correct ? 1 : 0);
    const newResults = [...results, { correct }];
    setScore(newScore);
    setResults(newResults);

    setTimeout(() => {
      if (round + 1 >= ROUNDS) {
        if (newScore > best) setBest(newScore);
        saveGameScore('tonedecoder', newScore);
        setPhase('done');
      } else {
        const next = round + 1;
        setRound(next);
        setPicked(null);
        setShuffled(shuffle(SCENARIOS[next].options));
      }
    }, 1100);
  };

  const col = '#5B9ED0';

  if (phase === 'idle') return (
    <div style={{ display:'flex',flexDirection:'column',gap:16,paddingTop:8 }}>
      <div style={s_GC({ padding:'20px 18px' })}>
        <div style={{ fontSize:9,color:col,letterSpacing:3,fontWeight:700,marginBottom:6 }}>
          {nl ? 'HOE HET WERKT' : 'HOW IT WORKS'}
        </div>
        <div style={{ fontSize:14,color:C.white,fontWeight:700,marginBottom:8 }}>
          {nl ? 'Lees het bericht. Welke emotie voelde de afzender het meest?' : 'Read the message. Which emotion did the sender most likely feel?'}
        </div>
        <div style={{ fontSize:12,color:C.silver,lineHeight:1.65,marginBottom:4 }}>
          {nl
            ? `${ROUNDS} berichten · Kies de juiste emotie · Empathie is een vaardigheid`
            : `${ROUNDS} messages · Pick the right emotion · Empathy is a skill`}
        </div>
        <div style={{ fontSize:10,color:`${col}80`,fontStyle:'italic',marginTop:8 }}>
          {nl
            ? 'Bron: Lopes et al. (2005) — emotionele toonherkenning is de kernvaardigheid van empathie.'
            : 'Source: Lopes et al. (2005) — emotional tone recognition is the core skill of empathy.'}
        </div>
      </div>
      <button style={s_PB({ width:'100%' })} onClick={startGame}>
        {nl ? '▶ Start Tone Decoder' : '▶ Start Tone Decoder'}
      </button>
    </div>
  );

  if (phase === 'done') return (
    <div style={{ display:'flex',flexDirection:'column',gap:12,paddingTop:8 }}>
      <div style={s_GC({ padding:'20px 18px', textAlign:'center' })}>
        <div style={{ fontSize:36,marginBottom:8 }}>
          {score >= 8 ? '🥇' : score >= 6 ? '🥈' : '🥉'}
        </div>
        <div style={{ fontSize:28,fontWeight:900,color:col,fontFamily:'Georgia,serif' }}>{score}/{ROUNDS}</div>
        <div style={{ fontSize:13,color:C.white,fontWeight:700,marginTop:4,marginBottom:8 }}>
          {score >= 8 ? (nl?'Uitstekend empatisch lezen!':'Excellent empathic reading!') :
           score >= 6 ? (nl?'Goed — je leest mensen goed.':'Good — you read people well.') :
           (nl?'Blijf oefenen — empathie groeit.':'Keep practising — empathy grows.')}
        </div>
        {best > 0 && <div style={{ fontSize:11,color:C.silver }}>
          {nl?`Beste: ${best}/${ROUNDS}`:`Best: ${best}/${ROUNDS}`}
        </div>}
        {/* Round-by-round dots */}
        <div style={{ display:'flex',justifyContent:'center',gap:6,marginTop:12,flexWrap:'wrap' }}>
          {results.map((r,i) => (
            <div key={i} style={{ width:24,height:24,borderRadius:'50%',background:r.correct?'#22c55e':'#ef4444',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12 }}>
              {r.correct?'✓':'✗'}
            </div>
          ))}
        </div>
      </div>
      <button style={s_PB({ width:'100%' })} onClick={startGame}>{nl?'Opnieuw':'Play again'}</button>
      <button onClick={onBack} style={{ background:'none',border:'none',color:C.silver,cursor:'pointer',fontSize:13,padding:8 }}>
        ← {nl?'Terug':'Back'}
      </button>
    </div>
  );

  // Playing
  return (
    <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
      {/* Progress */}
      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
        <div style={{ flex:1,height:4,background:'rgba(255,255,255,0.08)',borderRadius:4,overflow:'hidden' }}>
          <div style={{ height:'100%',width:`${(round/ROUNDS)*100}%`,background:`linear-gradient(90deg,${col},${col}aa)`,transition:'width 0.3s' }}/>
        </div>
        <div style={{ fontSize:11,color:C.silver,flexShrink:0 }}>{round+1}/{ROUNDS}</div>
        <div style={{ fontSize:11,color:col,fontWeight:700,flexShrink:0 }}>{score} ✓</div>
      </div>

      {/* Message card */}
      <div style={{ background:_T.bg3,border:`1px solid ${_T.cardBorderTop}`,borderRadius:16,padding:'20px 18px' }}>
        <div style={{ fontSize:9,color:col,letterSpacing:3,fontWeight:700,marginBottom:10 }}>
          {nl ? 'WAT VOELDE DE AFZENDER?' : 'WHAT DID THE SENDER FEEL?'}
        </div>
        <div style={{ fontSize:15,color:C.white,lineHeight:1.65,fontStyle:'italic',fontFamily:'Georgia,serif' }}>
          {current.msg}
        </div>
      </div>

      {/* Options */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8 }}>
        {shuffled.map(opt => {
          const isCorrect = opt === current.answer;
          const isPicked  = opt === picked;
          let bg = _T.bg3, border = `1px solid ${_T.cardBorderBot}`, textCol = C.white;
          if (picked !== null) {
            if (isCorrect)       { bg = '#22c55e18'; border = '1px solid #22c55e88'; textCol = '#22c55e'; }
            else if (isPicked)   { bg = '#ef444418'; border = '1px solid #ef444488'; textCol = '#ef4444'; }
          }
          return (
            <button key={opt} onClick={() => pick(opt)} style={{
              background:bg, border, borderRadius:12,
              padding:'12px 10px', cursor: picked ? 'default' : 'pointer',
              color:textCol, fontSize:13, fontWeight:600, textAlign:'center',
              transition:'all 0.25s',
            }}>
              {isPicked && picked !== null ? (isCorrect ? '✓ ' : '✗ ') : ''}{opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ToneDecoderGame;
